import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import BankVerificationFilter from "./BankVerificationFilter";
import TableFooter from '../Common/TableFooter';
import CommonExportButtons from '../Common/CommonExportButtons';
import PageSizeSelector from '../Common/PageSizeSelector';
import SkeletonTable from '../Common/SkeletonTable';

import { getAllUsersAction } from '../../../store/actions/user/UserActions';
import { getAllBanksAction } from '../../../store/actions/bank/BankActions';
import { getPropertyBankVerifyListsAction } from '../../../store/actions/propertyBankVerification/PropertyBankVerificationActions';

const BANK_VERIFICATION_AGENT_ID = process.env.REACT_APP_BANK_VERIFICATION_AGENT_ID;

const BankVerificationReport = () => {
  const dispatch = useDispatch();
  const { propertyBankVerifyLists, loading } = useSelector((state) => state.propertyBankVerification);
  const totalPages = propertyBankVerifyLists?.pages || 1;
  const propertyBankVerifyListRows = propertyBankVerifyLists?.rows || [];

  const { users } = useSelector((state) => state.users);
  const userRows = users?.rows || [];
  const { banks } = useSelector((state) => state.bank);
  const bankRows = banks?.rows || [];
  
  const [openMenu, setOpenMenu] = useState(true);
  const [status, setStatus] = useState("");
  const [user, setUser] = useState("");
  const [bank, setBank] = useState("");
  const [searchTerm, setSearchTerm] = useState('');
  const [filterQuery, setFilterQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [orderBy, setOrderBy] = useState('');
  const [orderByDir, setOrderByDir] = useState('desc');
  
  const statusOptions = [
    {value: 'all', label: 'All'},
    {value: 'pickedup', label: 'Picked up'},
    {value: 'verified', label: 'Verified'},
    {value: 'rejected', label: 'Rejected'}
  ];

  // Define skeleton table columns
  const skeletonColumns = [
    { title: 'Property Title', width: 150 },
    { title: 'Bank', width: 120 },
    { title: 'Agent', width: 120 },
    { title: 'Customer Name', width: 120 },
    { title: 'Phone', width: 100 },
    { title: 'Location', width: 150 },
    { title: 'Property Id', width: 100 },
    { title: 'Status', width: 80 },
    { title: 'Created', width: 120 }
  ];

  const buildSearchQuery = () => {
    const statusQuery = (!status || status === 'all') ? '' : status;
    const search = searchTerm ? searchTerm : '';
    const params = new URLSearchParams();
    
    if (statusQuery !== '') params.append("filter_status", statusQuery);
    if (user) params.append("filter_agentId", user);
    if (bank) params.append("filter_bankId", bank);
    if (search) params.append('search', search);
    if (orderBy) {
      params.append('orderBy', orderBy);
      params.append('orderByDir', orderByDir);
    }
    
    const queryString = `${params.toString()}`;
    return queryString;
  };

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrderByDir(orderByDir === 'desc' ? 'asc' : 'desc');
    } else {
      setOrderBy(field);
      setOrderByDir('desc');
    }
    setCurrentPage(1);
  };

  const onSubmit = async () => {
    const query = buildSearchQuery();
    setFilterQuery(query);
    setCurrentPage(1);
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'verified': return 'bg-success';
      case 'pickedup': return 'bg-info';
      case 'rejected': return 'bg-danger';
      default: return 'bg-secondary';
    }
  };

  const onBankkChange = async (id) => {
    console.log(id)
    setBank(id)
    dispatch(getAllUsersAction("filter_userRole="+BANK_VERIFICATION_AGENT_ID+"&filter_bankId="+id));
  };

  useEffect(() => {
    let query = buildSearchQuery();
    let effectivePageSize = pageSize;
    if (pageSize === 100000) {
      // 'All' option selected
      query = query ? `${query}&noPagination=true` : 'noPagination=true';
      effectivePageSize = 100000;
    }
    const populateQuery = `populate=propertyId,bankId,agentId,userId&${query}`;
    dispatch(getPropertyBankVerifyListsAction(currentPage, effectivePageSize, "", populateQuery));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentPage, pageSize, filterQuery, orderBy, orderByDir]);

  useEffect(() => {
    dispatch(getAllBanksAction());
  }, [dispatch]);

  return (
    <>
      <div className='form-head page-titles d-flex align-items-center'>
        <div className='me-auto d-lg-block'>
          <h4 className='mb-1'>Bank Verification Report</h4>
        </div>
      </div>
      <BankVerificationFilter
        setOpenMenu={setOpenMenu}
        openMenu={openMenu}
        onSubmit={onSubmit}
        statusOptions={statusOptions}
        setStatus={setStatus}
        userRows={userRows}
        setUser={setUser}
        banksRows={bankRows}
        onBankkChange={onBankkChange}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
      />
      
      <div className='row'>
        <div className="d-flex mb-2 align-items-center justify-content-between">
          <PageSizeSelector 
            pageSize={pageSize}
            setPageSize={setPageSize}
            setCurrentPage={setCurrentPage}
            options={[10, 50, 100]}
          />
          <CommonExportButtons
            data={propertyBankVerifyListRows}
            exportHeaders={[
              { label: 'Property Title', key: 'propertyId.title' },
              { label: 'Bank', key: 'bankId.name' },
              { label: 'Agent', key: 'agentId.name' },
              { label: 'Customer Name', key: 'userId.name' },
              { label: 'Phone', key: 'userId.phone' },
              { label: 'Location', key: 'propertyId.address' },
              { label: 'Property Id', key: 'propertyId.id' },
              { label: 'Status', key: 'status' },
              { label: 'Created', key: 'createdAt' },
            ]}
            filename="bank-verification-report"
            pdfTitle="Bank Verification Report"
            totalText="Total Bank Verifications"
            pdfOrientation="landscape"
            pdfHeaderColor={[111, 66, 193]}
            dateFields={['createdAt']}
          />
        </div>
        <div className='col-xl-12'>
          <div className='table-responsive table-hover fs-14 order-list-table'>
            {loading ? (
              <SkeletonTable columns={skeletonColumns} rows={8} />
            ) : (!loading && propertyBankVerifyListRows.length === 0) ? (
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 220, width: '100%' }}>
                <div className="card shadow-sm border-0 p-4 text-center w-100" style={{ background: '#f8fafc' }}>
                  <div style={{ fontSize: 48, color: '#adb5bd', marginBottom: 12 }}>
                    <i className="fa fa-search" />
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 20, color: '#495057', marginBottom: 4 }}>
                    No Data Found
                  </div>
                  <div style={{ color: '#868e96', fontSize: 15 }}>
                    Try adjusting your filters or search criteria.
                  </div>
                </div>
              </div>
            ) : (
              <table className='table display table-hover display mb-4 dataTablesCard card-table dataTable no-footer' id='bankVerificationTable'>
                <thead>
                  <tr role='row'>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('propertyId.title')}>
                      Property Title
                      <span style={{ marginLeft: 4, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
                        <i className="fa fa-caret-up" style={{ color: orderBy === 'propertyId.title' && orderByDir === 'asc' ? '#007bff' : '#ccc', opacity: orderBy === 'propertyId.title' || true ? 1 : 0.5, display: 'block', lineHeight: 1 }} />
                        <i className="fa fa-caret-down" style={{ color: orderBy === 'propertyId.title' && orderByDir === 'desc' ? '#007bff' : '#ccc', opacity: orderBy === 'propertyId.title' || true ? 1 : 0.5, display: 'block', lineHeight: 1, marginTop: -2 }} />
                      </span>
                    </th>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('bankId.name')}>
                      Bank
                      <span style={{ marginLeft: 4, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
                        <i className="fa fa-caret-up" style={{ color: orderBy === 'bankId.name' && orderByDir === 'asc' ? '#007bff' : '#ccc', opacity: orderBy === 'bankId.name' || true ? 1 : 0.5, display: 'block', lineHeight: 1 }} />
                        <i className="fa fa-caret-down" style={{ color: orderBy === 'bankId.name' && orderByDir === 'desc' ? '#007bff' : '#ccc', opacity: orderBy === 'bankId.name' || true ? 1 : 0.5, display: 'block', lineHeight: 1, marginTop: -2 }} />
                      </span>
                    </th>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('agentId.name')}>
                      Agent
                      <span style={{ marginLeft: 4, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
                        <i className="fa fa-caret-up" style={{ color: orderBy === 'agentId.name' && orderByDir === 'asc' ? '#007bff' : '#ccc', opacity: orderBy === 'agentId.name' || true ? 1 : 0.5, display: 'block', lineHeight: 1 }} />
                        <i className="fa fa-caret-down" style={{ color: orderBy === 'agentId.name' && orderByDir === 'desc' ? '#007bff' : '#ccc', opacity: orderBy === 'agentId.name' || true ? 1 : 0.5, display: 'block', lineHeight: 1, marginTop: -2 }} />
                      </span>
                    </th>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('userId.name')}>
                      Customer Name
                      <span style={{ marginLeft: 4, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
                        <i className="fa fa-caret-up" style={{ color: orderBy === 'userId.name' && orderByDir === 'asc' ? '#007bff' : '#ccc', opacity: orderBy === 'userId.name' || true ? 1 : 0.5, display: 'block', lineHeight: 1 }} />
                        <i className="fa fa-caret-down" style={{ color: orderBy === 'userId.name' && orderByDir === 'desc' ? '#007bff' : '#ccc', opacity: orderBy === 'userId.name' || true ? 1 : 0.5, display: 'block', lineHeight: 1, marginTop: -2 }} />
                      </span>
                    </th>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('userId.phone')}>
                      Phone
                      <span style={{ marginLeft: 4, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
                        <i className="fa fa-caret-up" style={{ color: orderBy === 'userId.phone' && orderByDir === 'asc' ? '#007bff' : '#ccc', opacity: orderBy === 'userId.phone' || true ? 1 : 0.5, display: 'block', lineHeight: 1 }} />
                        <i className="fa fa-caret-down" style={{ color: orderBy === 'userId.phone' && orderByDir === 'desc' ? '#007bff' : '#ccc', opacity: orderBy === 'userId.phone' || true ? 1 : 0.5, display: 'block', lineHeight: 1, marginTop: -2 }} />
                      </span>
                    </th>
                    <th>Location</th>
                    <th>Property Id</th>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('status')}>
                      Status
                      <span style={{ marginLeft: 4, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
                        <i className="fa fa-caret-up" style={{ color: orderBy === 'status' && orderByDir === 'asc' ? '#007bff' : '#ccc', opacity: orderBy === 'status' || true ? 1 : 0.5, display: 'block', lineHeight: 1 }} />
                        <i className="fa fa-caret-down" style={{ color: orderBy === 'status' && orderByDir === 'desc' ? '#007bff' : '#ccc', opacity: orderBy === 'status' || true ? 1 : 0.5, display: 'block', lineHeight: 1, marginTop: -2 }} />
                      </span>
                    </th>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('createdAt')}>
                      Created
                      <span style={{ marginLeft: 4, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
                        <i className="fa fa-caret-up" style={{ color: orderBy === 'createdAt' && orderByDir === 'asc' ? '#007bff' : '#ccc', opacity: orderBy === 'createdAt' || true ? 1 : 0.5, display: 'block', lineHeight: 1 }} />
                        <i className="fa fa-caret-down" style={{ color: orderBy === 'createdAt' && orderByDir === 'desc' ? '#007bff' : '#ccc', opacity: orderBy === 'createdAt' || true ? 1 : 0.5, display: 'block', lineHeight: 1, marginTop: -2 }} />
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {propertyBankVerifyListRows.map((item, idx) => (
                    <tr key={item._id}>
                      <td>
                        <div style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.propertyId?.title}>
                          {item.propertyId?.title || 'N/A'}
                        </div>
                      </td>
                      <td>{item.bankId?.name || 'N/A'}</td>
                      <td>{item.agentId?.name || 'N/A'}</td>
                      <td>{item.userId?.name || 'N/A'}</td>
                      <td>{item.userId?.phone || 'N/A'}</td>
                      <td>
                        <div style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.propertyId?.address}>
                          {item.propertyId?.address || 'N/A'}
                        </div>
                      </td>
                      <td>{item.propertyId?.id || item.propertyId?._id || 'N/A'}</td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(item.status)}`}>
                          {item.status?.charAt(0).toUpperCase() + item.status?.slice(1) || 'N/A'}
                        </span>
                      </td>
                      <td>{moment(item.createdAt).format("DD/MM/YYYY, hh:mm A")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
      {(!loading && propertyBankVerifyListRows.length > 0) && (
        <TableFooter
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalEntries={propertyBankVerifyLists?.total || propertyBankVerifyListRows.length}
          onPageChange={setCurrentPage}
        />
      )}
    </>
  );
};

export default BankVerificationReport;
